import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
    // Sep 2026: the backend now requires this on every /api/* route except
    // /api/health. Set at build time via REACT_APP_API_KEY — see README for
    // the deploy-time env var. Not a secret against someone inspecting this
    // deployed bundle (any build-time frontend value is visible client-side)
    // — it's a proportionate fix for "anyone with the bare API URL", not a
    // multi-user auth system.
    ...(process.env.REACT_APP_API_KEY ? { "X-API-Key": process.env.REACT_APP_API_KEY } : {}),
  },
});

export const fetcher = (url) => http.get(url).then((r) => r.data);

// Endpoints
export const getDashboardStats = () => fetcher("/dashboard/stats");
export const getSignals = (params = {}) => http.get("/signals", { params }).then((r) => r.data);
export const getSignal = (id) => fetcher(`/signals/${id}`);
export const getRejected = (limit = 100) => http.get("/rejected-signals", { params: { limit } }).then((r) => r.data);
export const getLogs = (params = {}) => http.get("/logs", { params }).then((r) => r.data);
export const getSettings = () => fetcher("/settings");
export const updateSettings = (payload) => http.put("/settings", payload).then((r) => r.data);
export const triggerScan = () => http.post("/scan/run").then((r) => r.data);
export const getScanRuns = (limit = 20) => http.get("/scan/runs", { params: { limit } }).then((r) => r.data);
export const testTelegram = (telegram_bot_token, telegram_chat_id) =>
  http.post("/telegram/test", { telegram_bot_token, telegram_chat_id }).then((r) => r.data);
export const getPairs = (limit = 100) => http.get("/pairs", { params: { limit } }).then((r) => r.data);
export const runBacktest = (payload) => http.post("/backtest", payload).then((r) => r.data);
export const getHealth = () => fetcher("/health");
export const getSignalStats = (days = 30) =>
  http.get("/signals/stats", { params: { days } }).then((r) => r.data);
export const triggerLifecyclePoll = () =>
  http.post("/lifecycle/poll").then((r) => r.data);
export const getLifecycleStatus = () => fetcher("/lifecycle/status");

// ----- Paper trading V1 (Phase 3A) -----
export const getPaperAccount = () => fetcher("/paper/account");
export const getPaperTrades = (params = {}) =>
  http.get("/paper/trades", { params }).then((r) => r.data);
export const getPaperEquity = () => fetcher("/paper/equity");
export const getPaperAnalytics = () => fetcher("/paper/analytics");
export const backfillPaper = (cutoff) =>
  http.post("/paper/backfill", cutoff ? { cutoff } : {}).then((r) => r.data);
export const resetPaperAccount = () => http.post("/paper/reset").then((r) => r.data);

// ----- Strategy selector (Phase 3B) -----
export const getSelectorSummary = () => fetcher("/strategy-selector/summary");

// ----- Strategy selector V2 — shadow/advisory (Phase 3B.2) -----
export const getSelectorV2Summary = () => fetcher("/strategy-selector-v2/summary");

// ----- AI Trade Review (Phase 3) -----
export const generateAiReview = (id) =>
  http.post(`/signals/${id}/ai-review`).then((r) => r.data);

export const formatUsd = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

export const formatSignedUsd = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `${v >= 0 ? "+" : "-"}$${Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

export const formatNum = (n, digits = 4) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v.toFixed(digits);
};

export const formatPct = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(digits)}%`;
};

// ----- Lifecycle helpers (Phase 2) -----
export const CLOSED_STATUSES = [
  "HIT_TP",
  "HIT_SL",
  "HIT_SL_PARTIAL_TP",
  "EXPIRED",
];

export const isClosed = (s) => CLOSED_STATUSES.includes(s?.status);

// R-multiple = blended PnL % / risk %, where risk = |SL distance %|.
export const signalRMultiple = (s) => {
  if (s?.pnl_percent === null || s?.pnl_percent === undefined) return null;
  const risk = Math.abs(Number(s?.sl_percent));
  if (!risk) return null;
  return Number(s.pnl_percent) / risk;
};

export const formatSignedPct = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
};

export const formatR = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}R`;
};

export const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dys = Math.floor(h / 24);
  return `${dys}d ago`;
};
